import { useRouter } from 'next/router';

import { useDeviceType } from '@packages/shared';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import type { GenericTrackingEvent } from '@packages/tracking';
import { useMouseflow, useTracking } from '@packages/tracking';

import type { ProductListDisplayMode } from '@packages/gql/generated/shopping/graphql';
import { type DesktopDisplayMode } from '../types';
import { decodeDisplayMode, encodeDisplayMode, pruneQuery } from '../queryEncoding';

const getInternalDisplayMode = (apiDisplayMode: ProductListDisplayMode) => {
  switch (apiDisplayMode) {
    case 'CARDS':
      return 'cards';
    case 'LARGE_CARDS':
      return 'largeCards';
    case 'MORE_INFORMATION':
      return 'moreInformation';
    default:
      throw new Error(`invalid display mode: ${apiDisplayMode}}`);
  }
};

export const UseDisplayModeFragment = graphql(/* GraphQL */ `
  fragment UseDisplayModeFragment on SearchProductResult {
    defaultDisplayMode
  }
`);

const mouseflowMapping = {
  cards: 'search_ViewChangeSmallPictures',
  largeCards: 'search_ViewChangeLargePictures',
  moreInformation: 'search_ViewChangeMoreInformation',
};

const gtmMapping = {
  cards: 'galerie_4_spaltig',
  largeCards: 'galerie_3_spaltig',
  moreInformation: 'liste',
};

export const useDisplayMode = (maskedData: FragmentType<typeof UseDisplayModeFragment>) => {
  const data = getFragmentData(UseDisplayModeFragment, maskedData);

  const pushMouseflowData = useMouseflow();
  const dispatchGTMEvent = useTracking();

  const { isDesktop } = useDeviceType();
  const { push, pathname, query } = useRouter();

  const defaultDisplayMode = getInternalDisplayMode(data.defaultDisplayMode);

  const displayMode = decodeDisplayMode(query) ?? defaultDisplayMode;

  const mobileDisplayMode = displayMode === 'largeCards' ? 'cards' : displayMode;

  const pushDisplayModeChange = (newDisplayMode: DesktopDisplayMode) => {
    push(
      {
        // currently the /c/ prefix is internal-only, and rewritten in the middleware
        pathname: pathname.replace(/^\/c/, ''),
        query: pruneQuery({
          ...query,
          ...encodeDisplayMode(newDisplayMode === defaultDisplayMode ? undefined : newDisplayMode),
        }),
      },
      undefined,
      { scroll: false },
    );

    dispatchGTMEvent<GenericTrackingEvent>({
      event: 'suche-event',
      eventValue: `view=${gtmMapping[newDisplayMode]}`,
    });

    pushMouseflowData(mouseflowMapping[newDisplayMode]);
  };

  return {
    displayMode: isDesktop ? displayMode : mobileDisplayMode,
    pushDisplayModeChange,
  };
};
